import {
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  DxButtonModule,
  DxCalendarModule,
  DxSchedulerComponent,
  DxSchedulerModule,
  DxSpeedDialActionModule,
  DxTooltipComponent,
  DxTooltipModule,
} from 'devextreme-angular';
import { LeftSidePanelComponent } from './left-side-panel/left-side-panel.component';
import { CalendarListComponent } from './calendar-list/calendar-list.component';
import DataSource from 'devextreme/data/data_source';
import { ScreenService } from '../../services/screen.service';
import { TakvimService } from './takvim.service';
import { SchedulerTooltipComponent } from './scheduler-tooltip/scheduler-tooltip.component';
import { ApplyPipeModule } from './apply.pipe';
import { Router } from '@angular/router';
import { ViewType } from 'devextreme/ui/scheduler';

type SelectedAppointment = { data: Record<string, any>; target: any };

@Component({
  selector: 'dk-takvim',
  standalone: true,
  imports: [
    CommonModule,
    DxButtonModule,
    DxCalendarModule,
    DxSchedulerModule,
    DxSpeedDialActionModule,
    DxTooltipModule,
    LeftSidePanelComponent,
    CalendarListComponent,
    SchedulerTooltipComponent,
    ApplyPipeModule,
  ],
  templateUrl: './takvim.component.html',
  styleUrls: ['./takvim.component.scss'],
})
export class TakvimComponent {
  @Input() selectedAppointmentData: Record<string, any>;

  @Output() clickDeleteAppointment = new EventEmitter<any>();
  @Output() clickEditAppointment = new EventEmitter<any>();

  currentDate = new Date();
  selectedAppointment: SelectedAppointment = null;

  @ViewChild('schedulerRef', { static: false })
  schedulerRef: DxSchedulerComponent;

  @ViewChild('tooltipRef', { static: false }) tooltipRef: DxTooltipComponent;
  tasks: DataSource<any> = new DataSource([]);

  currentView:ViewType = 'workWeek';

  isRightPanelOpen = false;

  listDataSource: any[];

  resourcesList: any[];

  agendaItems: any[] = [];

  isXSmall = this.screen.sizes['screen-small'];

  schedulerCurrentDate: Date = this.currentDate;

  constructor(
    private takvimService: TakvimService,
    protected screen: ScreenService,
    private router: Router
  ) {
    this.takvimService.getDefaultListDS().subscribe((data: any) => {
      this.listDataSource = data;
      this.resourcesList = data.reduce(
        (res: Record<string, any>[], calendarList: any) =>
          res.concat(calendarList.items),
        []
      );
    });

    screen.screenChanged.subscribe(({ isXSmall }) => {
      this.isXSmall = isXSmall;
      this.repaintScheduler();
    });
  }

  ngOnInit(): void {
    this.takvimService.getSchedulerTasks().subscribe((data) => {
      this.tasks = new DataSource(data);
      this.repaintScheduler();
    });
  }

  openRightPanelOpen() {
    this.isRightPanelOpen = true;
    this.repaintScheduler();
  }

  onCalendarDateChange = (date: any) => {
    this.currentDate = date;
    this.updateAgenda({ startDate: this.currentDate });
    this.repaintScheduler();
  };

  getSchedulerCurrentDate = (currentDate: any) => {
    const schedulerInstance = this.schedulerRef?.instance;
    const startViewDate = schedulerInstance?.getStartViewDate();
    const endViewDate = schedulerInstance?.getEndViewDate();

    if (
      this.schedulerCurrentDate.getMonth() !== currentDate.getMonth() ||
      (startViewDate && startViewDate > currentDate) ||
      (endViewDate && endViewDate < currentDate)
    ) {
      this.schedulerCurrentDate = currentDate;
    }

    return this.schedulerCurrentDate;
  };

  onCurrentViewChange = (view: any) => {
    this.currentView = view;

    if (this.currentView === 'month' && !this.screen.sizes['screen-x-small']) {
      this.isRightPanelOpen = true;
      this.updateAgenda({ startDate: this.currentDate });
    }

    this.repaintScheduler();
  };

  onSelectedDateChange = (e?: Date) => {
    const date = e instanceof Date ? e : new Date();
    this.currentDate = date;
    this.selectedAppointment = { data: { startDate: date }, target: undefined };
    this.updateAgenda({ startDate: date });
  };

  onCellClick = ({ cellData }: any) => {
    this.onSelectedDateChange(cellData.startDate);

    if (this.currentView === 'month' && cellData) {
      const cellAppointments = this.findAllAppointmentsForDay(cellData);

      if (cellAppointments.length > 1) {
        this.selectedAppointment = { data: cellData, target: null };
        this.agendaItems = cellAppointments;
        this.toggleRightPanelOpen(true);
      }
    }
  };

  calendarListChanged(selectedCalendars: any) {
    const filters = selectedCalendars
      .flatMap((calendar: any) => [['calendarId', '=', calendar.id], 'or'])
      .slice(0, -1);

    this.tasks?.filter(filters.length > 0 ? filters : null);

    this.tasks?.load();
    this.updateAgenda({ startDate: this.currentDate });
  }

  repaintScheduler() {
    setTimeout(() => this.schedulerRef?.instance.repaint(), 0);
  }

  getTooltipPosition = (
    selectedAppointment: SelectedAppointment,
    rightPanelOpen: boolean,
    isXSmall: boolean
  ) => {
    if (isXSmall) {
      return 'bottom';
    }
    const classList =
      selectedAppointment?.target?.classList ||
      selectedAppointment?.target?.[0]?.classList;
    return classList?.contains('dx-list') && rightPanelOpen ? 'left' : 'top';
  };

  toggleRightPanelOpen(isOpen?: any) {
    this.isRightPanelOpen = isOpen || !this.isRightPanelOpen;
    this.repaintScheduler();
  }

  showAppointmentCreationForm(appointment?: any) {
    this.schedulerRef?.instance.showAppointmentPopup(
      appointment?.data,
      !appointment
    );
  }

  findAllAppointmentsForDay = (selectedAppointment: any) => {
    if (!this.tasks) {
      return [];
    }
    const appointments = this.tasks.items();
    if (appointments.length === 0 || !selectedAppointment) {
      return [];
    }
    return appointments.filter((appointment) => {
      return (
        appointment.startDate.getDate() ===
          selectedAppointment.startDate.getDate() &&
        appointment.startDate.getMonth() ===
          selectedAppointment.startDate.getMonth()
      );
    });
  };

  updateAgenda = (appointmentData?: any) => {
    this.agendaItems = this.findAllAppointmentsForDay(appointmentData);
  };

  onAppointmentClick(e: any) {
    const appointmentData = e.appointmentData;
    this.selectedAppointment = {
      data: appointmentData,
      target: e.targetElement,
    };

    if (this.currentView === 'month') {
      this.updateAgenda(appointmentData);
      this.toggleRightPanelOpen(true);
    }
  }

  onAppointmentTooltipShowing = (e: any) => {
    e.cancel = true;
    const appointmentData = e.appointments[0].appointmentData;
    const isAppointmentCollectorClicked = (e: any) => {
      return e.targetElement?.[0]?.classList.contains(
        'dx-scheduler-appointment-collector'
      );
    };

    this.selectedAppointment = {
      data: appointmentData,
      target: e.targetElement,
    };

    if (this.currentView === 'month' || isAppointmentCollectorClicked(e)) {
      this.updateAgenda(appointmentData);
    }

    if (
      (this.currentView === 'month' && this.screen.sizes['screen-small']) ||
      isAppointmentCollectorClicked(e)
    ) {
      this.toggleRightPanelOpen(true);
    } else {
      this.tooltipRef?.instance.show();
    }
  };

  showAppointmentTooltip = (e: any) => {
    this.schedulerRef?.instance.showAppointmentTooltip(e.itemData, e.element);
  };

  editSelectedAppointment() {
    this.showAppointmentCreationForm(this.selectedAppointment);
    this.tooltipRef?.instance.hide();
  }

  deleteSelectedAppointment(appointmentData: any) {
    this.schedulerRef?.instance.deleteAppointment(
      this.selectedAppointment?.data
    );
    this.tooltipRef?.instance.hide();
    this.agendaItems = this.findAllAppointmentsForDay(appointmentData);
  }

  goBack() {
    this.router.navigate(['/main/dabnet']);
  }
}
